import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { api } from "src/api/endpoints";
import { formatWebsiteUrl } from "./DialogBoxWebSite";

const PMW_LOGIN_URL = "https://www.postermywall.com/index.php/authenticate/showlogin/?redirect=https%3A%2F%2Fwww.postermywall.com%2Findex.php";
const PMW_MY_DESIGNS_URL = "https://www.postermywall.com/index.php";

export default function DialogPMWEditSync({ isOpen, onClose, startupData, onApplyWidgetUrl }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [designsSearchKey, setDesignsSearchKey] = useState("");

  const getStoredCreds = useCallback(() => {
    try {
      return JSON.parse(window.localStorage.getItem("PMWcred") || "{}");
    } catch (e) {
      return {};
    }
  }, []);

  const creds = useMemo(() => getStoredCreds(), [getStoredCreds, isOpen]);
  const hasValidCreds = Boolean(creds?.access_token && creds?.user_id && creds?.expires_at > Date.now());

  const createPMWProfileUrl = useCallback(() => {
    return process.env.REACT_APP_PMW_PROFILE_URL || PMW_LOGIN_URL;
  }, []);

  const createPMWauthorizeUrl = useCallback(() => {
    const pmwClientID = process.env.REACT_APP_PMW_CLIENT_ID;
    const pmwRedirectUrl = process.env.REACT_APP_PMW_REDIRECT_URL;
    const pmwBaseURL = new URL("https://api.postermywall.com/v1/oauth/authorize");

    pmwBaseURL.searchParams.set("client_id", pmwClientID);
    pmwBaseURL.searchParams.set("redirect_uri", pmwRedirectUrl);
    pmwBaseURL.searchParams.set("response_type", "code");

    return pmwBaseURL.toString();
  }, []);

  const findBestMatchedDesign = useCallback(
    (designsData) => {
      if (!Array.isArray(designsData) || designsData.length === 0) {
        return null;
      }

      const widgetName = (startupData?.name || "").trim().toLowerCase();

      const exactNameMatch = designsData.find((design) => (design?.name || "").trim().toLowerCase() === widgetName);
      if (exactNameMatch) {
        return exactNameMatch;
      }

      const fuzzyNameMatch = designsData.find((design) => (design?.name || "").toLowerCase().includes(widgetName));
      if (fuzzyNameMatch) {
        return fuzzyNameMatch;
      }

      return designsData[0];
    },
    [startupData?.name],
  );

  const fetchUserDesigns = useCallback(async () => {
    if (!hasValidCreds) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await api.get(`/postermywall/users/${creds.user_id}/designs`, {
        headers: {
          Authorization: `Bearer ${creds.access_token}`,
        },
      });

      const list = Array.isArray(data) ? data : [];
      setDesigns(list);
      const matched = findBestMatchedDesign(list);
      setSelectedDesignId(matched?.id || "");
    } catch (error) {
      toast.error(error?.message || "Failed to load PosterMyWall designs");
    } finally {
      setIsLoading(false);
    }
  }, [creds.access_token, creds.user_id, findBestMatchedDesign, hasValidCreds]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDesigns([]);
    setSelectedDesignId("");
    setDesignsSearchKey("");
    fetchUserDesigns();
  }, [fetchUserDesigns, isOpen]);

  const designsFiltered = useMemo(() => {
    const searchKey = designsSearchKey.trim().toLowerCase();

    if (!searchKey) {
      return designs;
    }

    return designs.filter((design) => {
      const designName = design?.name?.toLowerCase() || "";
      return designName.includes(searchKey);
    });
  }, [designs, designsSearchKey]);

  const selectedDesign = useMemo(() => {
    return designs.find((design) => String(design.id) === String(selectedDesignId)) || null;
  }, [designs, selectedDesignId]);

  const handleApplyChanges = useCallback(async () => {
    if (!hasValidCreds) {
      toast.error("PosterMyWall session expired. Authorize again.");
      return;
    }

    if (!selectedDesignId) {
      toast.error("Select PosterMyWall design first.");
      return;
    }

    setIsApplying(true);
    try {
      const { data, status } = await api.get(`/postermywall/users/${creds.user_id}/designs/${selectedDesignId}/download`, {
        headers: {
          Authorization: `Bearer ${creds.access_token}`,
        },
      });

      if (status !== 200 || !data?.download_url) {
        throw new Error("PosterMyWall did not return a new download url");
      }

      await onApplyWidgetUrl({
        url: formatWebsiteUrl(data.download_url),
      });

      toast.success("PosterMyWall widget updated");
      onClose(false);
    } catch (error) {
      toast.error(error?.message || "Failed to apply PosterMyWall changes");
    } finally {
      setIsApplying(false);
    }
  }, [creds.access_token, creds.user_id, hasValidCreds, onApplyWidgetUrl, onClose, selectedDesignId]);

  const openEditorUrl = selectedDesign?.copy_url || createPMWProfileUrl();

  return (
    <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={() => onClose(false)}>
      <DialogTitle sx={{ fontWeight: 700 }}>PosterMyWall edit sync</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          If you finished editing postermywall design, click button below to apply changes.
        </Typography>

        {!hasValidCreds ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="body2" color="warning.main">
              PosterMyWall authorization is missing or expired.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" href={createPMWauthorizeUrl()} target="_blank" rel="noreferrer">
                Authorize PosterMyWall
              </Button>
              <Button variant="outlined" href={PMW_LOGIN_URL} target="_blank" rel="noreferrer">
                Open PMW login
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading your designs...</Typography>
              </Box>
            ) : (
              <Card variant="outlined" sx={{ py: 1 }}>
                <Box sx={{ px: 2, pb: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Button variant="outlined" href={openEditorUrl} target="_blank" rel="noreferrer">
                    Open design editor
                  </Button>
                  <Button variant="text" onClick={fetchUserDesigns}>
                    Refresh designs
                  </Button>
                  <Button variant="text" href={PMW_MY_DESIGNS_URL} target="_blank" rel="noreferrer">
                    Open PMW home
                  </Button>
                </Box>
                <Box sx={{ px: 2, pb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    label="Search for design"
                    value={designsSearchKey}
                    onChange={(event) => setDesignsSearchKey(event.target.value)}
                  />
                </Box>
                <CardContent sx={{ pt: 1, maxHeight: 320, overflowY: "auto" }}>
                  {designsFiltered.length > 0 ? (
                    <TableContainer>
                      <Table size="small" aria-label="designs table">
                        <TableBody>
                          {designsFiltered.map((design) => {
                            const isChecked = String(selectedDesignId) === String(design.id);
                            return (
                              <TableRow
                                key={design.id}
                                hover
                                onClick={() => setSelectedDesignId(isChecked ? "" : design.id)}
                                sx={{
                                  cursor: "pointer",
                                  "&.MuiTableRow-hover:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                  backgroundColor: isChecked ? "action.selected" : "inherit",
                                }}>
                                <TableCell width={56} padding="checkbox">
                                  <Checkbox
                                    edge="start"
                                    checked={isChecked}
                                    onChange={(event) => setSelectedDesignId(event.target.checked ? design.id : "")}
                                    onClick={(event) => event.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <a href={design.copy_url} target="_blank" rel="noreferrer">{design.name}</a>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {`${design.type} ${new Date(design.created_on * 1000).toLocaleDateString()}`}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No designs found in your account.</Typography>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose(false)}>Close</Button>
        <Button variant="contained" onClick={handleApplyChanges} disabled={!hasValidCreds || isLoading || isApplying || !selectedDesignId}>
          {isApplying ? "Applying..." : "Apply changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
